import React, { memo, useCallback, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import IpdTextField from "../../../../utils/IpdTextField";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import { toast } from "react-toastify";
import FormField from "../FormField";
import { useAdmissionDoctors } from "../../hooks/useAdmissionDoctors";

import { ADMISSION_COLORS as C } from "../../constants/admissionTheme";

function Admission({ patient, setPatient, updateEncounter, errors = {}, disabled = false }) {
  const {
    departmentOptions,
    departmentNames,
    admittingDoctors,
    doctorsByDeptMap,
    loadingAdmittingDoctors,
    loadingAttendingDoctors,
    loadAdmittingDoctors,
    fetchDoctorsForDept,
  } = useAdmissionDoctors();

  const attendingRows = patient.encounter.attendingDoctorRows || [];

  useEffect(() => {
    if (patient.encounter.department) {
      loadAdmittingDoctors(patient.encounter.department);
    }
  }, [patient.encounter.department, loadAdmittingDoctors]);

  useEffect(() => {
    attendingRows.forEach((row) => {
      if (row.department) fetchDoctorsForDept(row.department);
    });
  }, [attendingRows, fetchDoctorsForDept]);

  useEffect(() => {
    const doctorId = patient.encounter.admittingDoctorId;
    const doctorName = patient.encounter.admittingDoctorName;
    if (!doctorId || doctorName || !admittingDoctors.length) return;

    const match = admittingDoctors.find((doc) => doc.doctor_id === doctorId);
    if (match?.name) {
      updateEncounter("admittingDoctorName", match.name);
    }
  }, [
    patient.encounter.admittingDoctorId,
    patient.encounter.admittingDoctorName,
    admittingDoctors,
    updateEncounter,
  ]);

  const handleDepartmentChange = useCallback((departmentName) => {
    if (disabled) return;
    const selected = departmentOptions.find((d) => d.departmentName === departmentName);
    setPatient((s) => ({
      ...s,
      encounter: {
        ...s.encounter,
        department: departmentName,
        departmentId: selected?.departmentId || "",
        admittingDoctorId: "",
        admittingDoctorName: "",
        wardNo: "",
        bedTypeName: "",
        roomNo: "",
        bedId: "",
      },
    }));
    if (departmentName) {
      loadAdmittingDoctors(departmentName);
    }
  }, [departmentOptions, disabled, loadAdmittingDoctors, setPatient]);

  const handleAdmittingDoctorChange = useCallback((doctorId) => {
    if (disabled) return;
    const selected = admittingDoctors.find((doc) => doc.doctor_id === doctorId);
    setPatient((s) => ({
      ...s,
      encounter: {
        ...s.encounter,
        admittingDoctorId: doctorId,
        admittingDoctorName: selected?.name || "",
      },
    }));
  }, [disabled, admittingDoctors, setPatient]);

  const handleAddAttendingRow = useCallback(() => {
    if (disabled) return;
    setPatient((s) => ({
      ...s,
      encounter: {
        ...s.encounter,
        attendingDoctorRows: [
          ...(s.encounter.attendingDoctorRows || []),
          {
            rowId: String(Date.now() + Math.random()),
            department: "",
            doctor_id: "",
            notes: "",
          },
        ],
      },
    }));
  }, [disabled, setPatient]);

  const handleRemoveAttendingRow = useCallback((rowId) => {
    if (disabled) return;
    setPatient((s) => ({
      ...s,
      encounter: {
        ...s.encounter,
        attendingDoctorRows: (s.encounter.attendingDoctorRows || []).filter((r) => r.rowId !== rowId),
      },
    }));
  }, [disabled, setPatient]);

  const handleUpdateAttendingDept = useCallback((rowId, department) => {
    if (disabled) return;
    setPatient((s) => ({
      ...s,
      encounter: {
        ...s.encounter,
        attendingDoctorRows: (s.encounter.attendingDoctorRows || []).map((row) => (
          row.rowId === rowId ? { ...row, department, doctor_id: "" } : row
        )),
      },
    }));
    if (department) fetchDoctorsForDept(department);
  }, [disabled, fetchDoctorsForDept, setPatient]);

  const handleUpdateAttendingDoctor = useCallback((rowId, doctorId) => {
    if (disabled) return;
    const admittingDoctorId = patient.encounter.admittingDoctorId;
    if (doctorId && doctorId === admittingDoctorId) {
      toast.warning("This doctor is already selected as the Admitting Doctor.");
      return;
    }
    const isDuplicate = attendingRows.some(
      (row) => row.rowId !== rowId && row.doctor_id === doctorId
    );
    if (doctorId && isDuplicate) {
      toast.warning("This doctor is already selected as an Attending Doctor.");
      return;
    }
    setPatient((s) => ({
      ...s,
      encounter: {
        ...s.encounter,
        attendingDoctorRows: (s.encounter.attendingDoctorRows || []).map((row) => (
          row.rowId === rowId ? { ...row, doctor_id: doctorId } : row
        )),
      },
    }));
  }, [attendingRows, disabled, patient.encounter.admittingDoctorId, setPatient]);

  return (
    <Box>
      {disabled && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          Doctor assignment cannot be changed for an existing admission.
        </Typography>
      )}
      <Typography variant="subtitle1" fontWeight={700} color={C.textPrimary} sx={{ mb: 2 }}>
        Admitting Doctor
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormField label="Department">
            <IpdTextField
              select
              fullWidth
              size="small"
              value={patient.encounter.department || ""}
              onChange={(e) => handleDepartmentChange(e.target.value)}
              disabled={disabled}
              displayEmpty
            >
              <MenuItem value="">Select department</MenuItem>
              {departmentOptions.map((dep) => (
                <MenuItem
                  key={`${dep.departmentName}-${dep.departmentId || "no-id"}`}
                  value={dep.departmentName}
                >
                  {dep.departmentName}
                </MenuItem>
              ))}
            </IpdTextField>
          </FormField>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormField label="Doctor" required error={errors.admittingDoctor}>
            <IpdTextField
              select
              fullWidth
              size="small"
              value={patient.encounter.admittingDoctorId || ""}
              onChange={(e) => handleAdmittingDoctorChange(e.target.value)}
              disabled={disabled || !patient.encounter.department || loadingAdmittingDoctors}
              error={Boolean(errors.admittingDoctor)}
              displayEmpty
            >
              <MenuItem value="">
                {loadingAdmittingDoctors ? "Loading doctors…" : "Select doctor"}
              </MenuItem>
              {loadingAdmittingDoctors ? (
                <MenuItem disabled value="__loading">
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
                </MenuItem>
              ) : (
                admittingDoctors.map((doc) => (
                  <MenuItem key={doc.doctor_id} value={doc.doctor_id}>
                    {doc.name}
                  </MenuItem>
                ))
              )}
            </IpdTextField>
          </FormField>
        </Grid>
        <Grid item xs={12}>
          <FormField label="Provisional Diagnosis">
            <IpdTextField
              fullWidth
              size="small"
              value={patient.encounter.provisionalDiagnosis || ""}
              onChange={(e) => updateEncounter("provisionalDiagnosis", e.target.value)}
              disabled={disabled}
              placeholder="e.g. Acute appendicitis"
            />
          </FormField>
        </Grid>
      </Grid>

      <Accordion
        defaultExpanded={false}
        disableGutters
        elevation={0}
        sx={{
          borderRadius: "8px !important",
          border: `1px solid ${C.border}`,
          boxShadow: C.shadow.card,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: C.panelHeaderBg,
            borderRadius: "8px",
            minHeight: 56,
            "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1.5 },
          }}
        >
          <Typography fontWeight={700} fontSize="1.125rem" color={C.textPrimary}>
            Attending Doctors
          </Typography>
          {attendingRows.length > 0 && (
            <Chip
              label={`${attendingRows.length} Attending Doctor${attendingRows.length > 1 ? "s" : ""}`}
              size="small"
              sx={{
                bgcolor: C.chipInfoBg,
                color: C.chipInfoText,
                fontWeight: 600,
                fontSize: "0.75rem",
                height: 24,
                ml: "auto",
              }}
            />
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography fontWeight={600} fontSize="0.95rem" color={C.textPrimary}>
              Attending Doctors
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddAttendingRow}
              disabled={disabled}
            >
              Add Attending Doctor
            </Button>
          </Box>

          {attendingRows.length > 0 ? (
            <Stack spacing={2}>
              {attendingRows.map((row) => {
                const doctorsOptions = doctorsByDeptMap[row.department] || [];
                const isLoading = loadingAttendingDoctors[row.department];

                return (
                  <Box
                    key={row.rowId}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px dashed ${C.border}`,
                      bgcolor: C.pageBg,
                    }}
                  >
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} md={5}>
                        <FormField label="Department">
                          <IpdTextField
                            select
                            fullWidth
                            size="small"
                            value={row.department}
                            onChange={(e) => handleUpdateAttendingDept(row.rowId, e.target.value)}
                            disabled={disabled}
                            displayEmpty
                          >
                            <MenuItem value="">Select</MenuItem>
                            {departmentNames.map((dep) => (
                              <MenuItem key={dep} value={dep}>{dep}</MenuItem>
                            ))}
                          </IpdTextField>
                        </FormField>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <FormField label="Doctor">
                          <IpdTextField
                            select
                            fullWidth
                            size="small"
                            value={row.doctor_id}
                            onChange={(e) => handleUpdateAttendingDoctor(row.rowId, e.target.value)}
                            disabled={disabled || !row.department || isLoading}
                            displayEmpty
                          >
                            <MenuItem value="">
                              {isLoading ? "Loading doctors…" : "Select doctor"}
                            </MenuItem>
                            {isLoading ? (
                              <MenuItem disabled value="__loading">
                                <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
                              </MenuItem>
                            ) : (
                              doctorsOptions.map((doc) => (
                                <MenuItem key={doc.doctor_id} value={doc.doctor_id}>
                                  {doc.name}{doc.specialty ? ` (${doc.specialty})` : ""}
                                </MenuItem>
                              ))
                            )}
                          </IpdTextField>
                        </FormField>
                      </Grid>
                      <Grid item xs={12} md={2} display="flex" justifyContent={{ xs: "flex-start", md: "center" }}>
                        <IconButton
                          aria-label="Remove attending doctor"
                          onClick={() => handleRemoveAttendingRow(row.rowId)}
                          disabled={disabled}
                          sx={{ color: C.error.text }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Box
              sx={{
                p: 2.5,
                textAlign: "center",
                border: `1px dashed ${C.border}`,
                borderRadius: 2,
                color: C.textMuted,
                bgcolor: C.pageBg,
              }}
            >
              No attending doctors added yet. Click &quot;Add Attending Doctor&quot; to assign.
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default memo(Admission);
